/**
 * Acceptation d'une invitation.
 *
 * Cet écran se montre **avant** toute question de session : celui qui arrive n'a par
 * définition pas de compte. Il est atteint par `?invitation=<jeton>` sur la racine, et
 * non par une URL profonde — les fichiers statiques sont servis par le Worker, qui n'a
 * pas de repli SPA, et un lien qui tombe sur un 404 est un lien mort.
 *
 * Ce que l'écran dit avant de demander quoi que ce soit : **quelle organisation**, quelle
 * adresse, quel rôle. « Rejoindre une organisation » sans dire laquelle demanderait
 * d'accorder sa confiance à l'aveugle, et c'est exactement la forme d'un hameçonnage.
 *
 * L'adresse est affichée mais **non modifiable** : elle vient de l'invitation. La rendre
 * saisissable laisserait entrer sous une autre identité, ce qui viderait l'invitation de
 * son sens. Le serveur l'ignorerait de toute façon.
 *
 * Après acceptation, on recharge la racine plutôt que d'adopter la session en mémoire.
 * Deux raisons : le jeton quitte la barre d'adresse — donc l'historique du navigateur et
 * les journaux de tout ce qui a vu passer l'URL — et l'application repart d'un état propre
 * avec un cookie qu'elle n'a pas eu à se transmettre à elle-même.
 */

import { useEffect, useId, useState, type FormEvent } from "react";
import { AlertCircle, ArrowRight, Loader2, MailCheck } from "lucide-react";

import { Logo } from "./AppShell";
import { MIN_PASSWORD_LENGTH } from "./authApi";
import {
  ROLE_HINTS,
  ROLE_LABELS,
  acceptInvitation,
  describeInvitation,
  frenchDate,
  type InvitationTarget,
} from "./membersApi";
import { Button, Card, Input, cn } from "./ui/primitives";

export function InvitationScreen({ token }: { token: string }) {
  const [target, setTarget] = useState<InvitationTarget | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const champMdp = useId();
  const champNom = useId();

  useEffect(() => {
    let vivant = true;
    describeInvitation(token)
      .then((found) => {
        if (vivant) setTarget(found);
      })
      .catch((e: unknown) => {
        if (vivant) {
          setRefus(
            e instanceof Error ? e.message : "Ce lien n'est pas valide.",
          );
        }
      });
    return () => {
      vivant = false;
    };
  }, [token]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    acceptInvitation(token, password, fullName.trim() || null)
      .then(() => {
        // Racine sans paramètre : le jeton disparaît de l'URL, et l'application se
        // recharge avec le cookie que le serveur vient de poser.
        window.location.replace(window.location.origin + "/");
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "erreur inconnue");
        setBusy(false);
      });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-5 flex items-center gap-2">
          <Logo className="h-7 w-7" />
          <span className="text-lg font-semibold tracking-tight">Capalys</span>
        </div>

        {refus ? (
          <Card className="border-bad/40 bg-bad-soft px-4 py-4">
            <div className="flex gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-bad" />
              <div>
                <div className="text-[0.88rem] font-semibold text-bad">
                  Cette invitation n'est plus utilisable
                </div>
                <p className="mt-1 text-[0.82rem] text-muted">{refus}</p>
                <p className="mt-2 text-[0.8rem] text-muted">
                  <a className="text-accent underline" href="/">
                    Revenir à la page de connexion
                  </a>
                </p>
              </div>
            </div>
          </Card>
        ) : !target ? (
          <Card className="px-4 py-6 text-center text-[0.85rem] text-muted">
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-faint" />
            <p className="mt-2">Vérification du lien…</p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="border-b border-line bg-surface-2/60 px-4 py-3">
              <div className="flex items-start gap-2">
                <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <div>
                  <div className="text-[0.9rem] font-semibold tracking-tight">
                    Rejoindre « {target.org_name} »
                  </div>
                  <p className="mt-0.5 text-[0.8rem] text-muted">
                    Vous entrez comme{" "}
                    <strong>
                      {(ROLE_LABELS[target.role] ?? target.role).toLowerCase()}
                    </strong>{" "}
                    — {ROLE_HINTS[target.role] ?? ""} Ce lien est valable
                    jusqu'au {frenchDate(target.expires_at)}.
                  </p>
                </div>
              </div>
            </div>

            <form className="space-y-3 px-4 py-4" onSubmit={submit}>
              <div>
                <span className="mb-1 block text-[0.68rem] font-medium tracking-wide text-faint uppercase">
                  Votre adresse
                </span>
                {/* Affichée, jamais saisissable : elle vient de l'invitation. */}
                <div className="rounded-md border border-line bg-surface-2 px-2 py-1.5 font-mono text-[0.8rem] text-muted">
                  {target.email}
                </div>
              </div>

              <label className="block" htmlFor={champNom}>
                <span className="mb-1 block text-[0.68rem] font-medium tracking-wide text-faint uppercase">
                  Votre nom
                </span>
                <Input
                  id={champNom}
                  value={fullName}
                  placeholder="Marie Durand"
                  className="w-full"
                  disabled={busy}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </label>

              <label className="block" htmlFor={champMdp}>
                <span className="mb-1 block text-[0.68rem] font-medium tracking-wide text-faint uppercase">
                  Mot de passe
                </span>
                <Input
                  id={champMdp}
                  type="password"
                  value={password}
                  className="w-full"
                  autoFocus
                  disabled={busy}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <span className="mt-1 block text-[0.75rem] text-faint">
                  {/* Une seule phrase pour les deux cas : la personne sait, elle, si elle
                      a déjà un compte Capalys. Le serveur, lui, ne le dira jamais. */}
                  Au moins {MIN_PASSWORD_LENGTH} caractères. Si vous avez déjà
                  un compte Capalys avec cette adresse, saisissez son mot de
                  passe actuel.
                </span>
              </label>

              {error && (
                <div className="flex gap-2 rounded-md border border-bad/40 bg-bad-soft px-3 py-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-bad" />
                  <span className="text-[0.8rem] text-muted">{error}</span>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="md"
                className={cn("w-full")}
                disabled={busy || password.length === 0}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                Rejoindre l'organisation
              </Button>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
